"use client";

import { type FC, useRef, useState, type Ref } from "react";

import { Button } from "../Button";
import type { IconProp } from "../../props";
import { Icon } from "../Icon";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useHover } from "../../hooks/useHover";
import { useDebug } from "../../hooks/useDebug";
import { Popup } from "../Popup";
import { SelectList, type SelectListOption } from "../SelectList";
import { mergeRefs } from "../../utils/mergeRefs";
import { IconButton } from "../IconButton";

import {
  buttonStyle,
  iconActionStyle,
  iconContainerStyle,
} from "./AddButton.css";

export type AddButtonProps = IconProp & {
  value: string;
  type?: "none" | "add" | "cancel";
  disabled?: boolean;
  options: SelectListOption[];
  onSelect?: (option: SelectListOption) => void;
  size?: "xs" | "s";
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

export const AddButton: FC<AddButtonProps> = ({
  value,
  icon = "add",
  type = "add",
  disabled = false,
  options = [],
  onSelect,
  size = "s",
  ref,
  ...props
}) => {
  const { log } = useDebug();
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const { handleEnter, handleLeave, hasHover } = useHover({
    disabled,
  });

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSelect = (option: SelectListOption) => {
    log("lightBlue", "AddButton", "handleSelect", option);

    if (isOpen) {
      setIsOpen(false);
    }

    onSelect?.(option);
  };

  useClickOutside<HTMLDivElement | HTMLButtonElement>(
    [popupRef, buttonRef],
    () => {
      handleClose?.();
    },
  );

  const handleClick = () => {
    if (options.length === 1) {
      onSelect?.(options[0]);
    } else if (!isOpen) {
      setIsOpen(true);
    }
  };

  const active = isOpen && options.length > 0 && !disabled;

  return (
    <Popup
      ref={popupRef}
      active={active}
      onClose={handleClose}
      content={<SelectList options={options} onSelect={handleSelect} />}
      escape
      size="minimal"
      placement="bottomLeft"
    >
      <div ref={mergeRefs(ref, buttonRef)}>
        <Button
          variant={type === "add" ? "primary" : "background"}
          border={type === "cancel" ? "default" : "none"}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          size={size}
          onClick={handleClick}
          disabled={disabled}
          className={buttonStyle({ variant: "button" })}
          trailing={<AddIcon icon={icon} hasHover={hasHover} type={type} />}
          active={active}
          {...props}
        >
          {value}
        </Button>
        <IconButton
          variant={type === "add" ? "primary" : "background"}
          border={type === "cancel" ? "default" : "none"}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          active={active}
          size={size}
          onClick={handleClick}
          disabled={disabled}
          className={buttonStyle({ variant: "iconButton" })}
          rounded
        >
          <AddIcon icon={icon} hasHover={hasHover} type={type} />
        </IconButton>
      </div>
    </Popup>
  );
};

const AddIcon: FC<
  IconProp & { hasHover: boolean; type: AddButtonProps["type"] }
> = ({ icon, hasHover, type }) => (
  <div className={iconContainerStyle({ hasHover })}>
    <Icon icon={icon} size="s" />

    {type !== "none" && icon !== "add" && (
      <Icon
        icon={type === "add" ? "addThick" : "closeThick"}
        size="xs"
        className={iconActionStyle({ hasHover })}
      />
    )}
  </div>
);
